import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import Button from '../../../generic/Button/Button';
import TextInput from '../../../generic/Forms/TextInput';
import Icon, { icons, IconText } from '../../../generic/Icon';
import Toggle from '../../../generic/Toggle';
import Tooltip from '../../../generic/Tooltip';
import type { JiraCustomField } from '../../../../../shared/jira';

type JiraProjectSettingsCustomFieldsHeaderItemProps = {
  heading: string;
  tooltip?: string;
};

/**
 * Represents one of the "header" cells in the Custom Fields table.
 * This is a rewrite of app/components/modules/jira/CustomFields.tsx, and should take over once that is GA
 */
const JiraProjectSettingsCustomFieldsHeaderItem: FunctionComponent<JiraProjectSettingsCustomFieldsHeaderItemProps> = ({
  heading,
  tooltip,
}) => (
  <div className="jira-custom-fields__header__item">
    {heading}
    {tooltip && [
      ' ',
      (
        <Tooltip id="custom-fields-header-item-tooltip" key="tooltip" tooltip={tooltip}>
          <span className="inline-block">
            <Icon icon={icons.infoCircle} />
          </span>
        </Tooltip>
      ),
    ]}
  </div>
);

type JiraProjectSettingsCustomFieldsRowProps = JiraCustomField & {
  isDisabled: boolean;
  onUpdateField: (field: Partial<JiraCustomField>) => void;
  onDelete: () => void;
};

/**
 * Represents a single "row" in the custom fields "table".
 */
const JiraProjectSettingsCustomFieldsRow: FunctionComponent<JiraProjectSettingsCustomFieldsRowProps> = ({
  onUpdateField,
  onDelete,
  isDisabled,
  ...props
}) => (
  <div className="jira-custom-fields__row">
    <TextInput
      label="Field ID"
      placeholder="10000"
      size="small"
      value={props.fieldId}
      onChange={fieldId => onUpdateField({ fieldId })}
      isDisabled={isDisabled}
    />
    <TextInput
      label="Field Display Name"
      placeholder="Approvers"
      size="small"
      value={props.displayName}
      onChange={displayName => onUpdateField({ displayName })}
      isDisabled={isDisabled}
    />
    <TextInput
      label="Default Value"
      placeholder="optional"
      size="small"
      value={props.defaultValue}
      onChange={defaultValue => onUpdateField({ defaultValue })}
      isDisabled={isDisabled}
    />
    <div>
      <Toggle
        icons={false}
        checked={props.isRequired}
        onChange={isRequired => onUpdateField({ isRequired })}
        isDisabled={isDisabled}
      />
    </div>
    <div>
      <Button
        type="outline"
        size="medium"
        onClick={onDelete}
        icon={icons.trash}
        isDisabled={isDisabled}
      />
    </div>
  </div>
);

/**
 * Factory for creating a new custom field.
 */
const newCustomField = (): JiraCustomField => ({
  fieldId: '',
  displayName: '',
  defaultValue: '',
  isRequired: true,
});

type JiraCustomFieldsProps = {
  customFields: JiraCustomField[];
  isDisabled: boolean;
  onChange: (fields: JiraCustomField[]) => void;
};

/**
 * JiraCustomFields is a generic component for managing
 * jira custom fields, used in both the integration settings page
 * and the project settings page.
 */
const JiraCustomFields: FunctionComponent<JiraCustomFieldsProps> = ({
  customFields,
  isDisabled,
  onChange,
}) => {
  const onAddNewCustomField = useCallback(() =>
    onChange([...customFields, newCustomField()]), [onChange, customFields]);

  const getOnDeleteCustomField = useCallback((fieldIndex: number) => () =>
    onChange(customFields.filter((_field, index) => index !== fieldIndex)), [onChange, customFields]);

  const getOnUpdateField = useCallback((fieldIndex: number) => (updatedField: Partial<JiraCustomField>) =>
    onChange(customFields.map((field, index) => index !== fieldIndex
      ? field
      : {
          ...field,
          ...updatedField,
        })), [onChange, customFields]);

  return (
    <div className="jira-custom-customFields">
      {customFields.length > 0 && (
        <header className="jira-custom-fields__header">
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Field ID"
            tooltip="Found at the end of the URL when editing a custom field details at your Jira site (Settings > Issues > Custom Fields)"
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Field Display Name"
            tooltip="Name of the field when exporting an issue in FOSSA. Tip: Use the same name as in Jira to minimize confusion."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Default Value (optional)"
            tooltip="User-editable value that will appear in this field. If left blank, the field will be empty."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Required"
            tooltip="User can't export an issue to Jira until this field has a value."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem heading="Delete" />
        </header>
      )}
      {customFields.map(({ fieldId, displayName, defaultValue, isRequired }, index) => (
        <JiraProjectSettingsCustomFieldsRow
          fieldId={fieldId}
          displayName={displayName}
          defaultValue={defaultValue}
          isRequired={isRequired}
          isDisabled={isDisabled}
          onDelete={getOnDeleteCustomField(index)}
          onUpdateField={getOnUpdateField(index)}
        />
      ))}
      <footer className="jira-custom-fields__footer">
        <Button
          type="secondary"
          size="medium"
          onClick={onAddNewCustomField}
          isDisabled={isDisabled}
        >
          <IconText
            icon={icons.plus}
            text="Add Field"
          />
        </Button>
      </footer>
    </div>
  );
};

export default JiraCustomFields;
